/** 人工修正营销正文，使用版本与来源指纹保护并发编辑；原书正文保持不变。 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type ProjectChapter,
  type ProjectMutation,
  landingProjectQueryKey,
  useLandingProjectChapterContent,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { Dialog } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';
export function LandingChapterManualContentDialog({
  projectId,
  chapter,
  onChanged,
  onClose,
}: {
  projectId: string;
  chapter: ProjectChapter;
  onChanged: (result: ProjectMutation) => void;
  onClose: () => void;
}) {
  const query = useLandingProjectChapterContent(projectId, chapter.id);
  const client = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const content = draft ?? query.data?.content ?? '';
  const valid =
    content.trim().length > 0 && new TextEncoder().encode(content).length <= 5 * 1024 * 1024;
  const save = useMutation({
    mutationFn: (body: {
      content: string;
      expected_content_version_id: string | null;
      expected_source_sha256: string;
    }) => landingProjectsApi.manualContent(projectId, chapter.id, body),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      onChanged(result);
      toast(
        result.change.html_inputs_changed
          ? '营销正文已保存，人工修改 Diff 已记录'
          : '正文未变化，未创建重复版本',
        { tone: 'pos' },
      );
      onClose();
    },
  });
  return (
    <Dialog
      title={`人工修正第 ${chapter.chapter_index} 章营销正文`}
      subtitle={chapter.title}
      width={960}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={!query.data || query.isFetching || !!query.error || !valid || save.isPending}
            onClick={() => {
              const source = query.data;
              if (source)
                save.mutate({
                  content,
                  expected_content_version_id: source.content_version_id,
                  expected_source_sha256: source.source_sha256,
                });
            }}
          >
            保存营销正文
          </button>
        </>
      }
    >
      <div className="vstack" style={{ padding: 18, gap: 12 }}>
        <p className="muted">
          仅保存营销正文副本，不修改原书。修改保留人工 Diff，本项目受影响任务需重新生成并重新下载。
        </p>
        {query.isPending ? (
          <p>加载当前营销正文…</p>
        ) : query.error ? (
          <p role="alert">
            {query.error.message}
            <button type="button" className="btn" onClick={() => void query.refetch()}>
              重新加载正文
            </button>
          </p>
        ) : (
          <>
            {query.data?.source_stale && (
              <p className="chip chip-warn">原书正文已更新，保存前请核对当前营销内容。</p>
            )}
            <textarea
              aria-label="营销正文"
              dir="auto"
              value={content}
              onChange={(event) => setDraft(event.target.value)}
              disabled={save.isPending}
              style={{
                width: '100%',
                minHeight: 420,
                resize: 'vertical',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                background: 'var(--bg)',
                color: 'var(--fg)',
                padding: 12,
                font: 'inherit',
                lineHeight: 1.7,
              }}
            />
            {!valid && <p role="alert">正文不能为空，且不能超过 5MB。</p>}
          </>
        )}
        {save.error && (
          <div role="alert">
            <p style={{ color: 'var(--neg)' }}>{save.error.message}</p>
            <button
              type="button"
              className="btn"
              onClick={async () => {
                const fresh = await query.refetch();
                if (fresh.data && !fresh.error) {
                  setDraft(null);
                  save.reset();
                }
              }}
            >
              放弃草稿并重新加载当前版本
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
